import React, { useEffect, useState } from "react";
import { Edit3, Plus, RefreshCw, Trash2, X } from "lucide-react";
import { api } from "../../lib/api";

const emptyFeeRow = () => ({ className: "", admissionFee: 0, tuitionFee: 0, yearlyDevelopment: 0, examinationFee: 0 });
const emptySchedule = () => ({ label: "", value: "" });
const emptyForm = () => ({
  title: "Fee Structure of School",
  academicYear: "",
  description: "All fees are in Indian Rupees (₹).",
  rows: [emptyFeeRow()],
  notesText: "",
  paymentSchedule: [emptySchedule()],
  contactText: "",
  isActive: true,
});

function normalizeItem(item) {
  return {
    title: item?.title || "Fee Structure of School",
    academicYear: item?.academicYear || "",
    description: item?.description || "",
    rows: Array.isArray(item?.rows) && item.rows.length ? item.rows.map((row) => ({
      className: row.className || "",
      admissionFee: row.admissionFee ?? 0,
      tuitionFee: row.tuitionFee ?? 0,
      yearlyDevelopment: row.yearlyDevelopment ?? 0,
      examinationFee: row.examinationFee ?? 0,
    })) : [emptyFeeRow()],
    notesText: Array.isArray(item?.notes) ? item.notes.join("\n") : "",
    paymentSchedule: Array.isArray(item?.paymentSchedule) && item.paymentSchedule.length
      ? item.paymentSchedule.map((entry) => ({ label: entry.label || "", value: entry.value || "" }))
      : [emptySchedule()],
    contactText: item?.contactText || "",
    isActive: Boolean(item?.isActive),
  };
}

export default function FeeStructures() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const response = await api.feeStructures.list();
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function startCreate() {
    setEditingId("");
    setForm(emptyForm());
    setError("");
    setOpen(true);
  }

  function startEdit(item) {
    setEditingId(item._id);
    setForm(normalizeItem(item));
    setError("");
    setOpen(true);
  }

  function updateRow(index, key, value) {
    setForm((current) => ({
      ...current,
      rows: current.rows.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row),
    }));
  }

  function updateSchedule(index, key, value) {
    setForm((current) => ({
      ...current,
      paymentSchedule: current.paymentSchedule.map((entry, entryIndex) => entryIndex === index ? { ...entry, [key]: value } : entry),
    }));
  }

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        title: form.title.trim(),
        academicYear: form.academicYear.trim(),
        description: form.description.trim(),
        rows: form.rows.map((row) => ({
          className: row.className.trim(),
          admissionFee: Number(row.admissionFee || 0),
          tuitionFee: Number(row.tuitionFee || 0),
          yearlyDevelopment: Number(row.yearlyDevelopment || 0),
          examinationFee: Number(row.examinationFee || 0),
        })).filter((row) => row.className),
        notes: form.notesText.split("\n").map((value) => value.trim()).filter(Boolean),
        paymentSchedule: form.paymentSchedule.map((entry) => ({ label: entry.label.trim(), value: entry.value.trim() })).filter((entry) => entry.label && entry.value),
        contactText: form.contactText.trim(),
        isActive: form.isActive,
      };

      if (!payload.rows.length) throw new Error("At least one fee row is required.");
      if (editingId) await api.feeStructures.update(editingId, payload);
      else await api.feeStructures.create(payload);
      setOpen(false);
      await load();
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  }

  async function remove(item) {
    if (!window.confirm("Delete this fee structure?")) return;
    try {
      await api.feeStructures.remove(item._id);
      await load();
    } catch (deleteError) {
      setError(deleteError.message);
    }
  }

  return (
    <section className="admin-page">
      <div className="admin-page-head">
        <div><h2>Fee Structure</h2><p>Manage academic-year fee tables, notes and payment schedule.</p></div>
        <button className="admin-primary" onClick={startCreate}><Plus size={17} /> Add Fee Structure</button>
      </div>
      {error && <div className="admin-alert error">{error}</div>}
      <div className="admin-card">
        {loading ? <div className="admin-empty"><RefreshCw className="spin" /> Loading...</div> : !items.length ? <div className="admin-empty">No fee structure added yet.</div> : (
          <div className="admin-table-wrap"><table className="admin-table"><thead><tr><th>Academic Year</th><th>Title</th><th>Classes</th><th>Status</th><th>Actions</th></tr></thead><tbody>
            {items.map((item) => <tr key={item._id}><td><strong>{item.academicYear}</strong></td><td>{item.title}</td><td>{item.rows?.length || 0}</td><td><span className={`admin-badge ${item.isActive ? "success" : "muted"}`}>{item.isActive ? "Active" : "Hidden"}</span></td><td><div className="admin-actions"><button onClick={() => startEdit(item)}><Edit3 size={16} /></button><button className="danger" onClick={() => remove(item)}><Trash2 size={16} /></button></div></td></tr>)}
          </tbody></table></div>
        )}
      </div>

      {open && <div className="admin-modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && setOpen(false)}>
        <form className="admin-modal admin-modal-wide" onSubmit={submit}>
          <div className="admin-modal-head"><h3>{editingId ? "Edit Fee Structure" : "Add Fee Structure"}</h3><button type="button" className="admin-icon-btn" onClick={() => setOpen(false)}><X size={19} /></button></div>
          <div className="admin-form">
            <label><span>Title *</span><input required value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} /></label>
            <label><span>Academic Year *</span><input required placeholder="2026-27" value={form.academicYear} onChange={(event) => setForm({ ...form, academicYear: event.target.value })} /></label>
            <label className="full"><span>Description</span><textarea rows={2} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>

            <div className="full admin-repeat-section">
              <div className="admin-repeat-head"><strong>Fee Rows</strong><button type="button" className="admin-secondary" onClick={() => setForm({ ...form, rows: [...form.rows, emptyFeeRow()] })}><Plus size={15} /> Add Class</button></div>
              {form.rows.map((row, index) => <div className="admin-fee-row" key={index}>
                <input placeholder="Class name" required value={row.className} onChange={(event) => updateRow(index, "className", event.target.value)} />
                <input type="number" min="0" placeholder="Admission" value={row.admissionFee} onChange={(event) => updateRow(index, "admissionFee", event.target.value)} />
                <input type="number" min="0" placeholder="Tuition" value={row.tuitionFee} onChange={(event) => updateRow(index, "tuitionFee", event.target.value)} />
                <input type="number" min="0" placeholder="Development" value={row.yearlyDevelopment} onChange={(event) => updateRow(index, "yearlyDevelopment", event.target.value)} />
                <input type="number" min="0" placeholder="Examination" value={row.examinationFee} onChange={(event) => updateRow(index, "examinationFee", event.target.value)} />
                <button type="button" className="admin-delete-btn" disabled={form.rows.length === 1} onClick={() => setForm({ ...form, rows: form.rows.filter((_, rowIndex) => rowIndex !== index) })}><Trash2 size={15} /></button>
              </div>)}
            </div>

            <label className="full"><span>Important Notes (one note per line)</span><textarea rows={5} value={form.notesText} onChange={(event) => setForm({ ...form, notesText: event.target.value })} /></label>

            <div className="full admin-repeat-section">
              <div className="admin-repeat-head"><strong>Payment Schedule</strong><button type="button" className="admin-secondary" onClick={() => setForm({ ...form, paymentSchedule: [...form.paymentSchedule, emptySchedule()] })}><Plus size={15} /> Add Schedule</button></div>
              {form.paymentSchedule.map((entry, index) => <div className="admin-schedule-row" key={index}>
                <input placeholder="Fee label" value={entry.label} onChange={(event) => updateSchedule(index, "label", event.target.value)} />
                <input placeholder="Payment timing" value={entry.value} onChange={(event) => updateSchedule(index, "value", event.target.value)} />
                <button type="button" className="admin-delete-btn" onClick={() => setForm({ ...form, paymentSchedule: form.paymentSchedule.filter((_, entryIndex) => entryIndex !== index) })}><Trash2 size={15} /></button>
              </div>)}
            </div>

            <label className="full"><span>Fee Query Contact Text</span><input value={form.contactText} onChange={(event) => setForm({ ...form, contactText: event.target.value })} /></label>
            <label className="full"><span>Website Status</span><div className="admin-check"><input type="checkbox" checked={form.isActive} onChange={(event) => setForm({ ...form, isActive: event.target.checked })} /><em>Show this fee structure on website</em></div></label>
          </div>
          <div className="admin-modal-actions"><button type="button" className="admin-secondary" onClick={() => setOpen(false)}>Cancel</button><button type="submit" className="admin-primary" disabled={saving}>{saving ? "Saving..." : "Save"}</button></div>
        </form>
      </div>}
    </section>
  );
}
